/**
 * Deterministic pseudo-randomness.
 *
 * Lesson text is generated, not stored — but it must be *identical* on the
 * server and on the client or React will throw a hydration mismatch, and it
 * must be identical across a retry so "try again" means the same exercise.
 * So every draw goes through a seeded generator; `Math.random` is never used
 * anywhere in this module.
 */

/** FNV-1a — small, fast, good enough spread for seeding. */
export function hashSeed(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export interface Rng {
  /** Float in [0, 1). */
  next(): number;
  /** Integer in [0, max). */
  int(max: number): number;
  /** A random element. Returns undefined only for an empty array. */
  pick<T>(items: readonly T[]): T;
  /** A shuffled copy (Fisher–Yates). */
  shuffle<T>(items: readonly T[]): T[];
  /** `count` items sampled without replacement, cycling if the pool is small. */
  sample<T>(items: readonly T[], count: number): T[];
}

/** mulberry32 — 32-bit, seedable, uniform enough for content selection. */
export function createRng(seed: string | number): Rng {
  let state = (typeof seed === 'string' ? hashSeed(seed) : seed >>> 0) || 1;

  const next = () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const int = (max: number) => (max <= 0 ? 0 : Math.floor(next() * max));

  const shuffle = <T>(items: readonly T[]): T[] => {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = int(i + 1);
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  };

  return {
    next,
    int,
    pick: <T>(items: readonly T[]) => items[int(items.length)],
    shuffle,
    sample: <T>(items: readonly T[], count: number): T[] => {
      if (items.length === 0) return [];
      const out: T[] = [];
      while (out.length < count) {
        const round = shuffle(items);
        for (const item of round) {
          out.push(item);
          if (out.length === count) break;
        }
      }
      return out;
    },
  };
}
